/**
 * A tiffin order row on My Orders.
 *
 * Laid out like the donation row beside it — picture, reference, what it is,
 * status, then the dates and the amount — so the two read as one list, with
 * the blue stripe and the Tiffin tag telling them apart.
 */

import { memo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import {
  formatDate,
  formatDayRange,
  formatMeals,
  formatMoney,
  formatPeople,
  orderStatusMeta,
  serviceScheduleLabel,
  type TiffinOrderSummary,
} from '@mfa/domain';
import { colors, radius, shadow, space } from '../../theme';
import { Badge } from '../common/Badge';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { ORDER_TYPE_LOOK, OrderTypeTag } from './OrderTypeTag';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

const MEAL_GLYPH = { BREAKFAST: 'breakfast', LUNCH: 'lunch', DINNER: 'dinner' } as const;

export interface TiffinListItemProps {
  order: TiffinOrderSummary;
  onPress: (order: TiffinOrderSummary) => void;
}

export const TiffinListItem = memo(function TiffinListItem({ order, onPress }: TiffinListItemProps) {
  const t = useT();
  const pick = useLocalized();
  const meal = `${t(order.serviceLabel)}, ${formatDayRange(order.serviceDate, order.endDate)}`;
  return (
    <Pressable
      testID={`tiffin-order-${order.id}`}
      onPress={() => onPress(order)}
      accessibilityRole="button"
      accessibilityLabel={`${t('Tiffin')} ${order.publicReference}, ${meal}, ${t(order.statusLabel)}, ${formatMoney(order.totalAmount)}`}
      style={({ pressed }) => [styles.card, pressed && styles.pressed]}
    >
      <View style={styles.top}>
        <View style={styles.tile}>
          <Glyph name={MEAL_GLYPH[order.serviceSlot]} size={34} />
        </View>

        <View style={styles.body}>
          <View style={styles.headRow}>
            <OrderTypeTag type="TIFFIN" />
            <Text variant="label" tone="secondary">
              #{order.publicReference}
            </Text>
          </View>
          <Text variant="cardTitle" numberOfLines={2}>
            {meal}
          </Text>
          <View style={styles.statusRow}>
            <Text variant="caption" tone="secondary">
              {`${formatPeople(order.persons)} · ${t(serviceScheduleLabel[order.schedule])} · ${formatMeals(order.deliveries)}`}
            </Text>
            <Badge label={order.statusLabel} tone={orderStatusMeta[order.orderStatus].tone} />
          </View>
        </View>
      </View>

      <View style={styles.footer}>
        <View style={styles.dates}>
          <Text variant="caption" tone="secondary" numberOfLines={2}>
            {pick(order.itemsPreview, order.itemsPreviewUr)}
          </Text>
          <Text variant="caption" tone="tertiary">
            {t('Ordered')} {formatDate(order.createdAt)}
          </Text>
        </View>
        <Text variant="amountSmall" tone="brand">
          {formatMoney(order.totalAmount)}
        </Text>
      </View>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    borderLeftWidth: space.xs,
    borderLeftColor: ORDER_TYPE_LOOK.TIFFIN.accent,
    padding: space.md,
    gap: space.md,
    ...shadow('card'),
  },
  pressed: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  top: {
    flexDirection: 'row',
    gap: space.md,
  },
  tile: {
    width: 72,
    height: 72,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ORDER_TYPE_LOOK.TIFFIN.tint,
  },
  body: {
    flex: 1,
    gap: space.xxs,
  },
  headRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.xs,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: space.xs,
    gap: space.sm,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
    paddingTop: space.md,
    gap: space.sm,
  },
  dates: {
    flex: 1,
    gap: space.xxs,
  },
});
